import { useCallback } from 'react';
import { type FieldValues, type UseFormRegister, type UseFormReturn, type ValidationRule } from 'react-hook-form';

const requiredWhitespace =
  (required: string | true | ValidationRule<boolean> | undefined) =>
  <T>(value: T): true | string => {
    if (!required) {
      // Required is not set, so we don't need to validate whitespace
      return true;
    }

    const errorMessage = typeof required === 'string' ? required : 'Required';
    if (value == null || (typeof value === 'string' && value.trim().length === 0)) {
      return errorMessage;
    }

    return true;
  };

/**
 * Substitutes the "register" method of the form result from react-hook-form "useForm" with one that
 * ensures that whitespace only values are not allowed when a field is marked as required.
 * @param form
 * @returns
 */
export const useFormRequiredWhitespace = <T extends FieldValues>(form: UseFormReturn<T>): UseFormReturn<T> => {
  const register: UseFormRegister<T> = useCallback(
    (name, options) => {
      // inject whitespace validation into the rules
      const originalValidationRules =
        typeof options?.validate === 'function' ? { default: options.validate } : options?.validate;

      const wrappedOptions = {
        ...options,
        validate: {
          ...originalValidationRules,
          whitespaces: requiredWhitespace(options?.required),
        },
      };
      return form.register(name, wrappedOptions);
    },
    [form.register],
  );

  return {
    ...form,
    register,
  };
};
